import { AvatarChip } from "@/components/AvatarChip";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isWithdrawnBlock, undeliveredUserBlocks, type ChatBlock } from "@/features/chat/foldChatEvents";
import { QueuedMessages } from "@/features/chat/QueuedMessages";
import { Markdown } from "@/features/chat/ChatMarkdown";
import { useChatFold } from "@/features/chat/useChatBlocks";
import { backgroundTaskSucceeded, backgroundTaskTitle, backgroundToolHint, backgroundWait } from "@/features/chat/backgroundTasks";
import { useChatSubscription } from "@/features/chat/useChatSubscription";
import { useIsStopping } from "@/features/chat/optimisticStop";
import { selectChatStream, useChatStore } from "@/features/chat/chatStore";
import { presentTool, toolDelegation } from "@/features/chat/toolPresentation";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { useOrgContext } from "@/lib/OrgContext";
import { useShortcutCommand } from "@/lib/keyboardShortcuts";
import { isSubmitKeyPress, useSubmitShortcut } from "@/lib/submitShortcut";
import { ErrorState } from "@/components/ErrorState";
import { TaskApprovalPanel } from "@/features/chat/ApprovalCard";
import { AssistantConversationList } from "./AssistantConversationList";
import { NavigationIcon } from "@/components/NavigationIcon";
import { useDismissOnOutside } from "@/lib/dismissOnOutside";
import { resizeTextarea } from "@/features/chat/autoGrow";
import {
  assistantPanelIsExpanded,
  canEditQueuedAssistantMessage,
  hasUnreadReply,
  internalTaskPath,
  linkTaskReferences,
  parseSystemNotice,
  pendingForChat,
  useAssistantDrawer,
  type PendingAssistantMessage,
} from "./assistantStore";
import { AssistantScrollFollow } from "./assistantScroll";
import { assistantDeepLinkChatId, conversationAwaitingApproval, withoutAssistantDeepLink } from "./assistantApproval";
import { DRAFT_CHAT_ID, useAssistant } from "./useAssistant";

/**
 * The Rivets assistant, as a 400px right-hand drawer mounted once in
 * `OrgShell`. It stays mounted while closed so the open conversation keeps its
 * chat subscription — the drawer is a place you glance at, not a page you
 * navigate to, and a reply must be able to arrive while you are elsewhere.
 */
export function AssistantDrawer({ memberId }: { memberId?: string }) {
  const { orgId } = useOrgContext();
  const context = memberId ? JSON.stringify([memberId, orgId]) : null;
  const activeContext = useAssistantDrawer((state) => state.context);
  useLayoutEffect(() => {
    useAssistantDrawer.getState().activateContext(context);
    return () => useAssistantDrawer.getState().activateContext(null);
  }, [context]);
  // Do not mount a transcript or composer using the previous context, even for
  // the first render before the layout effect clears its state.
  if (!context || activeContext !== context) return null;
  return <ScopedAssistantDrawer key={context} orgId={orgId} context={context} />;
}

function ScopedAssistantDrawer({ orgId, context }: { orgId: string; context: string }) {
  const panel = useAssistantDrawer((state) => state.panel);
  const unread = useAssistantDrawer((state) => state.unread);
  const view = useAssistantDrawer((state) => state.view);
  const setPanel = useAssistantDrawer((state) => state.setPanel);
  const toggle = useAssistantDrawer((state) => state.toggle);
  const showList = useAssistantDrawer((state) => state.showList);
  const openConversation = useAssistantDrawer((state) => state.openConversation);
  const expanded = assistantPanelIsExpanded(panel);
  useAssistantDeepLink(orgId, context, openConversation);

  useShortcutCommand("toggleAssistant", () => {
    toggle();
    return true;
  });

  const activeChatId = view.kind === "conversation" ? view.chatId : undefined;
  // Once a conversation is open its state stays live even while the panel is
  // shut: that is what makes the unread dot honest.
  const assistant = useAssistant(orgId, context, panel !== "closed" || activeChatId !== undefined);
  const activeConversation = assistant.conversations.find((conversation) => conversation.chatId === activeChatId);
  const [draft, setDraft] = useState("");
  const submitShortcut = useSubmitShortcut();
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const focusComposer = () => window.requestAnimationFrame(() => composerRef.current?.focus());

  useEffect(() => {
    if (!expanded) return;
    composerRef.current?.focus();
  }, [expanded, activeChatId]);

  // One line to start, six at most, then it scrolls — the workspace composer's
  // rule at the size a 400px card can afford.
  useLayoutEffect(() => {
    resizeTextarea(composerRef.current, { minLines: 1, maxLines: 6 });
  }, [draft, panel]);

  const blocked = (!assistant.available && !activeChatId) || Boolean(assistant.busy);
  const chatQuery = useQuery({
    queryKey: qk.chat(activeChatId ?? ""),
    queryFn: () => client.chatDetail(activeChatId!),
    enabled: Boolean(activeChatId),
    staleTime: 30_000,
  });
  const running = chatQuery.data?.chat.status === "running" || chatQuery.data?.chat.status === "queued";
  const stopping = useIsStopping(chatQuery.data?.chat);
  // Started from a channel message nobody has approved: the server refuses
  // messages to it until its owner approves or rejects it.
  const awaitingApproval = Boolean(activeChatId) && conversationAwaitingApproval(activeConversation, chatQuery.data?.chat.status);
  const [resetPrompt, setResetPrompt] = useState(false);

  async function submit() {
    const text = draft.trim();
    if (!text || blocked || awaitingApproval) return;
    setDraft("");
    await assistant.send(text, activeChatId);
  }

  return (
    <>
    <aside
      className={`assistant-drawer is-${panel}`}
      // The phone's chrome differs by view, and only CSS knows it is a phone:
      // on the front door the top-left is a close button rather than a title,
      // and there is no "new conversation" to offer (FRNT-715).
      data-view={activeChatId ? "conversation" : "list"}
      aria-label="Rivets assistant"
      aria-hidden={panel === "closed"}
      onKeyDown={(event) => {
        // Escape puts the panel out of the way rather than throwing the
        // conversation away — the pill is one click from where you were. Below
        // the drawer breakpoint there is no pill to minimise to, so there
        // Escape means close, which is the only state mobile has.
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        setPanel(isCompactViewport() ? "closed" : "minimized");
      }}
    >
      {/* Two groups rather than one row of siblings. On a desktop card they
          are `display: contents` and the header lays out exactly as before; on
          a phone each becomes a floating cluster on glass, which is the only
          way to give the transcript the whole screen (FRNT-715). */}
      <header className="assistant-header">
        <div className="assistant-header-lead">
          {/* The phone's way out, shown only where the phone's stylesheet says
              so; the card's own close button is the one in the actions group. */}
          <button
            type="button"
            className="assistant-icon-button assistant-close-lead"
            aria-label="Close assistant"
            title="Close"
            onClick={() => setPanel("closed")}
          >
            <NavigationIcon name="close" />
          </button>
          {activeChatId ? (
            <button type="button" className="assistant-icon-button" aria-label="Back to conversations" onClick={showList}>
              <NavigationIcon name="chevronLeft" />
            </button>
          ) : null}
          <h2 className="assistant-heading truncate">
            {activeConversation ? activeConversation.title || "Conversation" : "Assistant"}
          </h2>
        </div>
        <div className="assistant-header-actions">
          <button
            type="button"
            className="assistant-icon-button assistant-new-button"
            aria-label="New conversation"
            title="New conversation"
            onClick={() => { showList(); focusComposer(); }}
          >
            <NavigationIcon name="plus" />
          </button>
          <AssistantOverflowMenu
            busy={Boolean(assistant.busy)}
            activeChatId={awaitingApproval ? undefined : activeChatId}
            onRefresh={assistant.refetch}
            onArchive={() => { if (activeChatId) void assistant.archive(activeChatId); }}
            onReset={() => setResetPrompt(true)}
          />
          <button type="button" className="assistant-icon-button assistant-window-button" aria-label="Minimise assistant" title="Minimise" onClick={() => setPanel("minimized")}>
            <NavigationIcon name="minus" />
          </button>
          <button
            type="button"
            className="assistant-icon-button assistant-window-button"
            aria-label={panel === "maximized" ? "Restore assistant" : "Maximise assistant"}
            title={panel === "maximized" ? "Restore" : "Maximise"}
            onClick={() => setPanel(panel === "maximized" ? "open" : "maximized")}
          >
            <NavigationIcon name={panel === "maximized" ? "collapse" : "expand"} />
          </button>
          <button type="button" className="assistant-icon-button assistant-close-trailing" aria-label="Close assistant" title="Close" onClick={() => setPanel("closed")}>
            <NavigationIcon name="close" />
          </button>
        </div>
      </header>

      {resetPrompt ? (
        <div className="assistant-panel-notice" role="alert">
          <p>Reset the workspace? Existing conversations will be archived.</p>
          <button type="button" className="assistant-dismiss" onClick={() => setResetPrompt(false)}>Keep workspace</button>
          <button type="button" className="assistant-dismiss" disabled={Boolean(assistant.busy)} onClick={() => {
            setResetPrompt(false);
            void assistant.reset();
          }}>Reset workspace</button>
        </div>
      ) : null}
      {assistant.error ? (
        <div className="assistant-panel-notice assistant-error" role="alert">
          {assistant.error}
          <button type="button" className="assistant-dismiss" onClick={assistant.dismissError}>Dismiss</button>
        </div>
      ) : null}
      <div className="assistant-body">
        {activeChatId ? (
          <AssistantTranscript key={activeChatId} chatId={activeChatId} orgId={orgId} awaitingApproval={awaitingApproval} />
        ) : assistant.isError ? (
          <ErrorState title="Could not load the assistant" onRetry={assistant.refetch} />
        ) : assistant.isPending ? (
          <p className="assistant-note">Loading…</p>
        ) : (
          <>
            {/* The unavailable notice explains why the composer is off; it is
                not a reason to hide what the assistant is *for*, so the empty
                state stays above it. */}
            {assistant.conversations.length === 0 ? (
              draft.trim().length === 0
                ? <AssistantEmpty starting={assistant.starting} onPick={(prompt) => { setDraft(prompt); focusComposer(); }} />
                : null
            ) : (
              <>
                <div className="assistant-list-lead">
                  <button type="button" className="assistant-new-conversation" onClick={() => { showList(); focusComposer(); }}>
                    <NavigationIcon name="plus" />
                    New conversation
                  </button>
                </div>
                <AssistantConversationList
                  conversations={assistant.conversations}
                  activeChatId={activeChatId}
                  onSelect={openConversation}
                  archiveDisabled={Boolean(assistant.busy)}
                  onArchive={(chatId) => void assistant.archive(chatId)}
                />
              </>
            )}
            {!assistant.available ? <AssistantUnavailable reason={assistant.unavailableReason} /> : null}
          </>
        )}
        {activeChatId ? null : <FailedDrafts />}
      </div>

      <form
        className="assistant-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* One bordered box holding the field and its footer, so the send
            button is centred in a row of its own rather than trying to match
            the height of a textarea that grows. */}
        <div className="assistant-composer-box">
          <textarea
            ref={composerRef}
            className="assistant-composer-input"
            rows={1}
            value={draft}
            placeholder={awaitingApproval ? ASSISTANT_AWAITING_APPROVAL_TEXT : blocked ? "Assistant unavailable" : activeChatId ? "Reply…" : "Ask the assistant…"}
            disabled={blocked || awaitingApproval}
            aria-label="Message the assistant"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
              if (!isSubmitKeyPress(event, submitShortcut)) return;
              event.preventDefault();
              void submit();
            }}
          />
          <div className="assistant-composer-footer">
            {/* Reserved for the attachment/skills controls the workspace
                composer carries; the row keeps its shape without them. */}
            <span className="assistant-composer-slot" />
            {running && activeChatId && stopping ? (
              // The press lands at once; the chat's status ends it.
              <span className="composer-stopping" role="status">Stopping…</span>
            ) : running && activeChatId ? (
              <button type="button" className="assistant-send assistant-send-stop" disabled={Boolean(assistant.busy)} onClick={() => void assistant.cancel(activeChatId)} aria-label="Stop this turn" title="Stop">
                <StopGlyph />
              </button>
            ) : null}
            <button type="submit" className="assistant-send" disabled={blocked || awaitingApproval || draft.trim().length === 0} aria-label="Send message" title="Send">
              <SendGlyph />
            </button>
          </div>
        </div>
      </form>
    </aside>
    {panel === "minimized" ? (
      <div className="assistant-pill">
        <button type="button" className="assistant-pill-body" onClick={() => setPanel("open")} aria-label="Restore assistant">
          <span className="assistant-pill-glyph">
            <AssistantSparkles />
            {unread ? <span className="assistant-pill-unread" aria-label="Unread reply" /> : null}
          </span>
          <span>Assistant</span>
        </button>
        <button type="button" className="assistant-icon-button" aria-label="Maximise assistant" title="Maximise" onClick={() => setPanel("maximized")}>
          <NavigationIcon name="expand" />
        </button>
        <button type="button" className="assistant-icon-button" aria-label="Close assistant" title="Close" onClick={() => setPanel("closed")}>
          <NavigationIcon name="close" />
        </button>
      </div>
    ) : null}
    </>
  );
}

/**
 * `?assistant=<chatId>` — the link the Slack "waiting for approval" reply
 * carries — opens the drawer on that conversation, where its approval card
 * is, and is then dropped from the URL so a reload or a shared link does not
 * keep reopening it. The state is read fresh first: a conversation started a
 * moment ago is not in a list loaded before it, and opening a chat the list
 * lacks bounces straight back to the list.
 */
function useAssistantDeepLink(orgId: string, context: string, openConversation: (chatId: string) => void) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const chatId = assistantDeepLinkChatId(search);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Dropping the param re-runs the effect with no chat id, so the pending
  // open is cancelled only by unmounting (another account or org), not by that.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!chatId) return;
    void navigate({ to: ".", search: (previous: Record<string, unknown>) => withoutAssistantDeepLink(previous), replace: true });
    void queryClient
      .fetchQuery({ queryKey: [...qk.assistant(orgId), context], queryFn: () => client.assistantState(orgId), staleTime: 0 })
      .catch(() => undefined)
      .then(() => { if (mounted.current) openConversation(chatId); });
  }, [chatId, context, navigate, openConversation, orgId, queryClient]);
}

/** The header's `⋯`: the three things you do to the workspace rather than to
 * the message you are writing. Anchored to its own trigger, dismissed by the
 * app's four gestures like every other floating card. */
function AssistantOverflowMenu({ busy, activeChatId, onRefresh, onArchive, onReset }: {
  busy: boolean;
  activeChatId?: string;
  onRefresh: () => void;
  onArchive: () => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const refs = useMemo(() => [rootRef], []);
  // Anchored to its own trigger: without that, the transcript scrolling itself
  // to the bottom on every streamed frame closed this menu instantly.
  const dismissOptions = useMemo(() => ({ anchor: triggerRef }), []);
  useDismissOnOutside(open, refs, () => setOpen(false), dismissOptions);
  const run = (action: () => void) => { setOpen(false); action(); };
  return (
    <div className={`assistant-menu${open ? " open" : ""}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="assistant-icon-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More assistant actions"
        title="More"
        onClick={() => setOpen((value) => !value)}
      >
        <NavigationIcon name="ellipsis" />
      </button>
      {open ? (
        <div className="menu-select-popover menu-select-popover-end assistant-menu-popover" role="menu">
          <div className="menu-select-list">
            <button className="menu-select-option" type="button" role="menuitem" disabled={busy} onClick={() => run(onRefresh)}>
              <span className="menu-select-option-copy"><span className="menu-select-option-label">Refresh</span></span>
            </button>
            <button className="menu-select-option" type="button" role="menuitem" disabled={busy || !activeChatId} onClick={() => run(onArchive)}>
              <span className="menu-select-option-copy"><span className="menu-select-option-label">Archive conversation</span></span>
            </button>
            <button className="menu-select-option" type="button" role="menuitem" disabled={busy} onClick={() => run(onReset)}>
              <span className="menu-select-option-copy"><span className="menu-select-option-label">Reset workspace</span></span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** The same mark the sidebar row carries, at the size the panel needs. */
function AssistantSparkles({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1.5 9.1 5 12.5 6 9.1 7 8 10.5 6.9 7 3.5 6 6.9 5 8 1.5Z" fill="currentColor" />
      <path d="M12.5 10.5l.55 1.7 1.7.55-1.7.55-.55 1.7-.55-1.7-1.7-.55 1.7-.55.55-1.7Z" fill="currentColor" opacity=".65" />
    </svg>
  );
}

function SendGlyph() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 11.5v-9M3.2 6.3 7 2.5l3.8 3.8" />
    </svg>
  );
}

function StopGlyph() {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true">
      <rect x="4.5" y="4.5" width="5" height="5" rx="1" fill="currentColor" />
    </svg>
  );
}

/** A first message that never became a conversation, kept until dismissed so a
 * failed start is not silently swallowed by the list. */
function FailedDrafts() {
  const pending = useAssistantDrawer((state) => state.pending);
  const dropPending = useAssistantDrawer((state) => state.dropPending);
  const drafts = pendingForChat(pending, DRAFT_CHAT_ID).filter((message) => message.failed);
  if (drafts.length === 0) return null;
  return (
    <div className="assistant-draft-failures">
      {drafts.map((message) => (
        <div key={message.id} className="assistant-message assistant-message-user failed">
          {message.text}
          <div className="assistant-error">{message.failed}</div>
          <button type="button" className="assistant-dismiss" onClick={() => dropPending(message.id)}>Dismiss</button>
        </div>
      ))}
    </div>
  );
}

function AssistantUnavailable({ reason }: { reason?: string }) {
  return (
    <div className="assistant-note assistant-unavailable">
      <strong>The assistant has no workspace</strong>
      <p>{reason ?? "The assistant needs a repository and an online worker that supports MCP servers."}</p>
      <p className="hint">
        Start a worker with <code className="mono">rivets start</code> on a machine that has your repository, then reopen
        this drawer.
      </p>
    </div>
  );
}

/** Below the sidebar's drawer breakpoint the panel is a full-height overlay
 * with nothing to minimise to, so it offers close only — the same rule the
 * stylesheet applies to the minimise and maximise buttons. */
function isCompactViewport(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia("(max-width: 768px)").matches;
}

/** The four things people actually open this panel to do. A chip fills the
 * composer and hands the caret over; it never sends, because the prompt is a
 * starting point and the next word is usually a repository name. */
const ASSISTANT_SUGGESTIONS = [
  "Create a task",
  "What's running right now?",
  "Summarise my open Linear issues",
  "Check on my pull requests",
] as const;

function AssistantEmpty({ starting, onPick }: { starting: boolean; onPick: (prompt: string) => void }) {
  return (
    <div className="assistant-empty">
      <span className="assistant-empty-glyph"><AssistantSparkles size={20} /></span>
      <p className="assistant-empty-title">{starting ? "Starting…" : "Ask the assistant"}</p>
      <p className="assistant-empty-copy">Delegate work to a task, check on what's running, or ask about your Linear issues.</p>
      <div className="assistant-empty-chips">
        {ASSISTANT_SUGGESTIONS.map((prompt) => (
          <button key={prompt} type="button" className="assistant-suggestion" onClick={() => onPick(prompt)}>{prompt}</button>
        ))}
      </div>
    </div>
  );
}

/**
 * A compact transcript. It reuses the ordinary chat subscription and block
 * fold, then renders a narrower row set than the workspace stream: prose,
 * one-line tool activity, and system notices. Reasoning, usage and response
 * ownership are dropped — in a 400px column they are noise.
 */
const ASSISTANT_AWAITING_APPROVAL_TEXT = "Approve this conversation before sending more messages.";

function AssistantTranscript({ chatId, orgId, awaitingApproval }: { chatId: string; orgId: string; awaitingApproval: boolean }) {
  useChatSubscription(chatId);
  const fold = useChatFold(chatId);
  const blocks = fold.blocks;
  const stream = useChatStore(selectChatStream(chatId));
  const navigate = useNavigate();
  const expanded = useAssistantDrawer((state) => assistantPanelIsExpanded(state.panel));
  const pending = useAssistantDrawer((state) => state.pending);
  const settlePending = useAssistantDrawer((state) => state.settlePending);
  const markUnread = useAssistantDrawer((state) => state.markUnread);
  const scrollRef = useRef<HTMLDivElement>(null);
  const seenSeqRef = useRef<{ chatId: string; seq: number }>({ chatId, seq: stream.lastSeq });

  const chatQuery = useQuery({
    queryKey: qk.chat(chatId),
    queryFn: () => client.chatDetail(chatId),
    staleTime: 30_000,
  });
  // Only the author may rewrite their own queued message.
  const meQuery = useQuery({ queryKey: qk.me(), queryFn: () => client.me(), staleTime: 30_000 });
  const currentUserId = meQuery.data?.user.id;
  const running = chatQuery.data?.chat.status === "running" || chatQuery.data?.chat.status === "queued";
  // Once the card has been shown here, its decided banner stays for this
  // visit — it is only looked up while the conversation is waiting.
  const [approvalSeen, setApprovalSeen] = useState(awaitingApproval);
  useEffect(() => { if (awaitingApproval) setApprovalSeen(true); }, [awaitingApproval]);
  const wait = useMemo(() => running ? backgroundWait(fold.backgroundTasks) : null, [running, fold.backgroundTasks]);
  const optimistic = pendingForChat(pending, chatId);
  // A message sent to an idle assistant starts its run as far as the reader is
  // concerned: "Working…" shows from the press, under the message, rather than
  // once the chat's status catches up.
  const working = running || optimistic.some((message) => !message.failed);

  // Every user message the server has a seq for. This is the *other* pending
  // layer — an optimistic echo lives here only until its own send comes back,
  // which is true of a queued message too. Deliberately reads `blocks`, not the
  // transcript below: a message pulled out for the dock is still acknowledged.
  const serverAcknowledged = useMemo(
    () => blocks.flatMap((block) => (block.kind === "user" ? [block] : [])),
    [blocks],
  );
  useEffect(() => {
    settlePending(chatId, serverAcknowledged);
  }, [chatId, settlePending, serverAcknowledged, pending]);

  // Messages the assistant has not started are stacked above the composer
  // rather than left where they landed, so its work on the turn in progress
  // cannot bury the thing it has not read yet. One its author deleted is not
  // shown at all.
  const undelivered = useMemo(() => undeliveredUserBlocks(blocks), [blocks]);
  const undeliveredIds = useMemo(() => new Set(undelivered.map((block) => block.id)), [undelivered]);
  const transcript = useMemo(
    () => blocks.filter((block) => !undeliveredIds.has(block.id) && !isWithdrawnBlock(block)),
    [blocks, undeliveredIds],
  );

  // Unread: anything the assistant said past what was on screen the last time
  // the panel was showing it. The user's own messages never count.
  useEffect(() => {
    if (seenSeqRef.current.chatId !== chatId) seenSeqRef.current = { chatId, seq: stream.lastSeq };
    if (expanded) {
      seenSeqRef.current = { chatId, seq: stream.lastSeq };
      return;
    }
    if (hasUnreadReply(stream.events, seenSeqRef.current.seq)) markUnread();
  }, [chatId, markUnread, expanded, stream.events, stream.lastSeq]);

  const scrollFollow = useRef(new AssistantScrollFollow());
  const [following, setFollowing] = useState(true);
  function followLatest() {
    const element = scrollRef.current;
    if (element) setFollowing(scrollFollow.current.jumpToLatest(element));
  }
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!expanded || !element) return;
    setFollowing(scrollFollow.current.update(element, pendingForChat(pending, chatId).at(-1)?.id));
  }, [blocks, expanded, pending, running, chatId]);

  // What has not even left this browser yet. Sent into a running assistant it
  // sits below the activity, next to the queue its echo will join; what the
  // server is holding for a later turn is in that queue under the transcript.
  const undeliveredDock = optimistic.length > 0 ? (
    <div className="assistant-undelivered-dock">
      {optimistic.map((message) => (
        <PendingRow key={message.id} message={message} />
      ))}
    </div>
  ) : null;

  // Task references the assistant emits are ordinary links in Markdown; keeping
  // them inside the SPA is a click interception, not a second renderer.
  function onTranscriptClick(event: MouseEvent<HTMLDivElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const anchor = (event.target as HTMLElement).closest("a");
    if (!anchor) return;
    const path = internalTaskPath(anchor.getAttribute("href") ?? "", window.location.origin);
    if (!path) return;
    event.preventDefault();
    navigate({ to: path });
  }

  return (
    <>
      <div className="assistant-transcript-container">
        <div className="assistant-transcript" ref={scrollRef} onClick={onTranscriptClick} onScroll={() => {
          if (!scrollRef.current) return;
          setFollowing(scrollFollow.current.observeScroll(scrollRef.current));
        }}>
          {!stream.loaded && blocks.length === 0 ? <p className="assistant-note">Loading conversation…</p> : null}
          {transcript.map((block) => (
            <AssistantRow key={block.id} block={block} orgId={orgId} />
          ))}
          {/* Above the activity when it starts the run, as its echo will be. */}
          {running ? null : undeliveredDock}
          {working ? (
            <>
              <div className="assistant-activity" aria-live="polite">
                <span className="assistant-running-dot" />
                {wait ? wait.label : "Working…"}
              </div>
              {wait ? [...wait.descriptions, ...(wait.overflow ? [wait.overflow] : [])].map((line, index) => (
                <div key={index} className="assistant-activity" style={{ paddingLeft: 14 }}>
                  <span className="assistant-activity-summary truncate" title={line}>{line}</span>
                </div>
              )) : null}
            </>
          ) : null}
          {running ? undeliveredDock : null}
        </div>
        {!following ? <button type="button" className="assistant-latest" onClick={followLatest}>Jump to latest ↓</button> : null}
      </div>
      {/* Outside the scroller, pinned above the composer: the approval a
          channel message is waiting on, or the record of who decided it. */}
      {awaitingApproval || approvalSeen ? (
        <div className="assistant-approval">
          <TaskApprovalPanel chatId={chatId} enabled={awaitingApproval} awaiting={awaitingApproval} />
        </div>
      ) : null}
      {/* Outside the scroller, so it stays pinned just above the composer. */}
      <QueuedMessages
        className="assistant-queued-messages"
        chatId={chatId}
        messages={undelivered}
        // A `<rivets_system>` notice is the server's framing, not its author's,
        // so a message carrying one can be deleted or sent now but not rewritten.
        canEdit={(block) => canEditQueuedAssistantMessage(block, currentUserId)}
        canManage={(block) => block.queuedBehindRun && block.userId === currentUserId}
        bodyOf={(block) => { const { notice, body } = parseSystemNotice(block.text); return body || notice || ""; }}
      />
    </>
  );
}

function AssistantRow({ block, orgId }: { block: ChatBlock; orgId: string }) {
  const { membersById } = useOrgContext();
  switch (block.kind) {
    case "user": {
      const { notice, body } = parseSystemNotice(block.text);
      return (
        <>
          {notice ? <div className="assistant-system-notice">{notice}</div> : null}
          {body ? <div className="assistant-message assistant-message-user">
            <div className="assistant-message-head">
              <AvatarChip id={block.userId} name={membersById.get(block.userId)?.name ?? "User"} image={membersById.get(block.userId)?.image} avatarUrls={membersById.get(block.userId)?.avatarUrls} size={18} />
              <span className="hint truncate">{membersById.get(block.userId)?.name ?? "User"}</span>
            </div>
            {body}
          </div> : null}
        </>
      );
    }
    case "assistant":
      return (
        <div className="assistant-message assistant-message-agent">
          {block.text ? <Markdown text={linkTaskReferences(block.text, orgId)} /> : <span className="assistant-caret" />}
          {block.failed ? <div className="assistant-error">{block.failed}</div> : null}
        </div>
      );
    case "tool": {
      const presentation = presentTool(block.name, block.input, block.output, block.isError);
      const delegation = toolDelegation(block.name, block.input);
      // A backgrounded call's result is a receipt for the request, so the row
      // has to keep saying the work is going after that result arrives.
      const backgroundHint = block.done ? backgroundToolHint(block) : null;
      return (
        <div
          className={`assistant-activity${block.isError ? " failed" : ""}`}
          // The drawer is a flat list, so nesting is all the indent can say.
          style={block.parentToolUseId ? { paddingLeft: 14 } : undefined}
        >
          <span className="assistant-activity-label truncate">{delegation?.label ?? presentation.label}</span>
          {presentation.summary ? <span className="assistant-activity-summary truncate">{presentation.summary}</span> : null}
          {backgroundHint ? <span className="assistant-activity-summary">{backgroundHint.toLowerCase()}</span> : null}
        </div>
      );
    }
    case "job-status":
      if (block.status !== "failed" && block.status !== "canceled") return null;
      return (
        <div className="assistant-activity failed">
          {block.status === "failed" ? block.error ?? "The assistant's turn failed." : "Turn canceled."}
        </div>
      );
    case "background-task":
      return (
        <div
          className={`assistant-activity${backgroundTaskSucceeded(block.status) ? "" : " failed"}`}
          // The drawer has no room to open a summary in place; it stays one hover away.
          title={block.summary}
        >
          <span className="assistant-activity-label truncate">{backgroundTaskTitle(block.status)}</span>
          {block.description ? <span className="assistant-activity-summary truncate">{block.description}</span> : null}
        </div>
      );
    case "log":
      return block.level === "error" ? <div className="assistant-activity failed">{block.message}</div> : null;
    default:
      // Reasoning, usage, response ownership and handoff context are workspace
      // detail; the drawer shows the conversation, not the run.
      return null;
  }
}

function PendingRow({ message }: { message: PendingAssistantMessage }) {
  const dropPending = useAssistantDrawer((state) => state.dropPending);
  return (
    <div className={`assistant-message assistant-message-user${message.failed ? " failed" : " pending"}`}>
      {message.text}
      {message.failed ? (
        <>
          <div className="assistant-error">{message.failed}</div>
          <button type="button" className="assistant-dismiss" onClick={() => dropPending(message.id)}>Dismiss</button>
        </>
      ) : null}
    </div>
  );
}
